import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../../store/appStore';
import { useRegion } from '../../hooks/useRegion';
import { Card, CardContent } from '../ui/card';
import { calculateROI } from '../../lib/utils';
import { rankByProfit } from '../../lib/equipmentInsights';
import { signedPercent } from './signedPercent';

export function MVPEquipmentCard({ variant = 'mvp' }) {
  const navigate = useNavigate();
  const { equipment } = useAppStore();
  const { formatCurrency } = useRegion();

  const activeEquipment = rankByProfit(equipment);

  const item = variant === 'mvp' ? activeEquipment[0] : activeEquipment[1];
  const isMVP = variant === 'mvp';

  // The MVP is the one inverse, highlighted panel in its row; the runner-up
  // is a plain surface panel beside it.
  const panelClass = isMVP
    ? 'rounded-highlight bg-inverse text-inverse-foreground'
    : 'rounded-tile bg-surface text-foreground';
  const mutedText = isMVP ? 'text-inverse-muted' : 'text-muted-foreground';
  const header = (
    <p className={`eyebrow ${isMVP ? 'text-inverse-muted' : ''}`}>
      {isMVP ? 'mvp · most profit' : 'runner-up'}
    </p>
  );

  if (!item) {
    return (
      <Card className={`h-full border-0 ${panelClass}`}>
        <CardContent className="p-6">
          <div className="mb-3">{header}</div>
          <p className={`text-sm ${mutedText}`}>
            {isMVP ? 'Start renting to see your top performer!' : 'Rent more equipment to see rankings'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const roi = calculateROI(item.total_income || 0, item.purchase_price || 0);
  const profitNegative = (item.profit || 0) < 0;
  const facts = [
    `${signedPercent(roi, 0).replace(/^\+/, '')} ROI`,
    `${item.total_days || 0} rental days`,
    isMVP && item.purchase_price ? `bought for ${formatCurrency(item.purchase_price)}` : null,
  ].filter(Boolean);

  return (
    <Card
      className={`cursor-pointer transition-colors h-full flex flex-col border-0 ${panelClass} ${isMVP ? '' : 'hover:bg-mist'}`}
      onClick={() => navigate(`/equipment/${item.id}`)}
    >
      <CardContent className="p-6 flex-1 flex flex-col gap-3">
        <p className={`eyebrow ${isMVP ? 'text-inverse-muted' : ''}`}>
          {isMVP ? 'mvp · most profit' : 'runner-up'}
        </p>
        <h3 className="section-title line-clamp-2">{item.name}</h3>
        <p
          className={`mt-auto text-3xl xl:text-5xl 2xl:text-6xl font-light tracking-tight leading-none tabular-nums ${!isMVP && profitNegative ? 'text-brick' : ''}`}
          title="Total profit: income less what it cost"
        >
          {formatCurrency(item.profit || 0)}
        </p>
        <p className={`text-sm ${mutedText}`}>{facts.join(' · ')}</p>
      </CardContent>
    </Card>
  );
}
